import { ActionTree } from 'vuex';
import { createDefaultField } from '../../helpers/field-default.helper';
import { FieldPlacementHelper } from '../../helpers/field-placement.helper';
import { FieldHelper } from '../../helpers/field.helper';
import { IField } from '../../types/field';
import { IClickCreateFieldPayload } from '../types/click-create-field-payload';
import { IDropCreateFieldPayload } from '../types/drop-create-field-payload';
import {
  ECreateFieldAction,
  ECreateFieldMutation,
  state as defaultState,
} from './create-field.constants';
import { ICreateFieldState } from './create-field.types';
import { RootState } from '@/store/store';
import { translate } from '@/helpers/translation.helper';
import { EnumDocumentType } from '@/enums/DocumentType';
import { FieldService } from '@/api/fields/services/field.service';

export const actions: ActionTree<ICreateFieldState, RootState> = {
  [ECreateFieldAction.Reset]({ commit }) {
    commit(ECreateFieldMutation.SetState, { ...defaultState });
  },

  [ECreateFieldAction.ClickCreate]({ dispatch }, payload: IClickCreateFieldPayload) {
    const { pageId, lastUpdatedField, pageWidth } = payload;
    const lastUpdatedFieldHasSamePage: boolean = lastUpdatedField?.pageId === pageId;
    const defaultField: IField = createDefaultField(
      pageId,
      payload.documentId,
      payload.type,
      pageWidth,
      payload.connectFirstRecipient,
      payload.mergeValue,
      payload.sender,
      payload.recipient?.id,
      null,
      null,
      lastUpdatedFieldHasSamePage && payload.lastUpdatedField
    );
    const field: IField = {
      ...defaultField,
      id: FieldHelper.getTempId(),
      position: FieldPlacementHelper.calculatePosition(payload.pageFields, defaultField, pageWidth),
      isLoading: true,
      isEditable: payload.documentType !== EnumDocumentType.Template,
    };
    dispatch(ECreateFieldAction.Create, field);
  },

  [ECreateFieldAction.DropCreate]({ dispatch }, payload: IDropCreateFieldPayload) {
    const defaultField: IField = createDefaultField(
      payload.pageId,
      payload.documentId,
      payload.type,
      payload.pageWidth,
      payload.connectFirstRecipient,
      payload.mergeValue,
      payload.sender,
      payload.recipient?.id,
      payload.top,
      payload.left,
      null
    );
    const field: IField = {
      ...defaultField,
      id: FieldHelper.getTempId(),
      position: FieldPlacementHelper.calculatePosition(
        payload.pageFields,
        defaultField,
        payload.pageWidth
      ),
      isLoading: true,
      isEditable: payload.documentType !== EnumDocumentType.Template,
    };
    dispatch(ECreateFieldAction.Create, field);
  },

  async [ECreateFieldAction.Create]({ commit, dispatch }, field: IField) {
    commit(ECreateFieldMutation.SetOptimisticallyCreatedField, field);
    try {
      const id: string = await FieldService.create(field);
      dispatch(ECreateFieldAction.CreateSuccess, {
        tempId: field.id,
        field: { ...field, id, isLoading: false },
      });
    } catch {
      dispatch(ECreateFieldAction.CreateFailed, field.id);
    }
  },

  [ECreateFieldAction.CreateFailed]({ dispatch, commit }, fieldId: string) {
    commit(ECreateFieldMutation.SetUnsuccessfullyCreatedFieldId, fieldId);
    dispatch(`alert/showDangerAlert`, translate('could-not-create-field').toString(), {
      root: true,
    });
  },

  [ECreateFieldAction.CreateSuccess](
    { commit },
    { tempId, field }: { tempId: string; field: IField }
  ) {
    commit(ECreateFieldMutation.SetCreatedFieldData, {
      field,
      tempId,
    });
  },
};
